import React from "react";
import EmptyStateCard from "../../../components/shared/EmptyStateCard";
import DetailMetricCard from "../../../components/shared/DetailMetricCard";
import PageHero from "../../../components/shared/PageHero";
import StatusPanel from "../../../components/shared/StatusPanel";
import SurfaceCard from "../../../components/shared/SurfaceCard";
import RichiesteService from "../../../services/RichiesteService";
import UserService from "../../../services/UserService";
import {
  getSellerOfferFeedbackPanel,
  getSellerOrderStatePanel,
  getSellerSubmitOfferButtonState,
} from "../../../utils/marketplaceStatus";
import { getRichiestaExpiresAt, isRichiestaExpired } from "../../../utils/richiestaExpiry";
import { formatDateTime } from "../../../utils/date";
import "../../../styles/screens/marketplace/seller/RichiesteRicevute.css";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} EUR`;
const getRequestDisplayTitle = (richiesta) => {
  const title = (richiesta?.titolo || "").trim();
  if (title && !/^Richiesta farmaci/i.test(title)) {
    return title;
  }

  const prodotti = Array.isArray(richiesta?.prodotti) ? richiesta.prodotti : [];
  if (prodotti.length === 0) {
    return title || "Ordine marketplace";
  }

  const firstName = prodotti[0]?.nome || "Prodotto";
  return prodotti.length === 1
    ? `Ordine ${firstName}`
    : `Ordine ${firstName} +${prodotti.length - 1}`;
};

class RichiestaDettaglioVenditore extends React.Component {
  state = {
    richiesta: null,
    productDiscounts: {},
    orderDiscountPercent: "",
    orderDiscountAmount: "",
    shippingCost: "",
    isLoading: true,
    isSubmitting: false,
    isTakingCharge: false,
    error: "",
    submitError: "",
    submitSuccess: "",
    takeChargeError: "",
    takeChargeSuccess: "",
  };

  componentDidMount() {
    this.loadRichiesta();
  }

  getSellerIdentity = () => {
    const parsed = UserService.getParsedToken() || {};
    const displayName = [parsed.given_name, parsed.family_name].filter(Boolean).join(" ").trim();

    return {
      id: parsed.preferred_username || parsed.email || parsed.sub || "seller",
      name: parsed.name || displayName || parsed.preferred_username || parsed.email || "Venditore Marketplace",
    };
  };

  buildInitialDiscounts = (richiesta) => {
    const seller = this.getSellerIdentity();
    const existingOffer = (richiesta?.offerte || []).find((item) => item.sellerId === seller.id);
    const discounts = {};

    (richiesta?.prodotti || []).forEach((prodotto) => {
      const currentLine = existingOffer?.voci?.find((voce) => voce.descrizione === prodotto.nome);
      const baseLineTotal = Number(prodotto.prezzoIndicativo || 0) * Number(prodotto.quantita || 0);
      let percent = "";
      let amount = "";

      if (currentLine && baseLineTotal > 0) {
        const lineAmount =
          Number(currentLine.scontoValore || 0) ||
          Math.max(0, Number((baseLineTotal - Number(currentLine.prezzo || 0)).toFixed(2)));
        amount = String(lineAmount);
        percent = String(
          Number(currentLine.scontoPercentuale || 0) ||
            Math.max(0, Math.min(100, Math.round((lineAmount / baseLineTotal) * 100)))
        );
      }

      discounts[prodotto.id || prodotto.nome] = { percent, amount };
    });

    return { discounts, existingOffer };
  };

  loadRichiesta = async () => {
    this.setState({
      isLoading: true,
      error: "",
      submitError: "",
      submitSuccess: "",
      takeChargeError: "",
      takeChargeSuccess: "",
    });

    try {
      const richiesta = await RichiesteService.getById(this.props.match.params.richiestaId);
      const { discounts, existingOffer } = this.buildInitialDiscounts(richiesta);
      const baseTotal = (richiesta.prodotti || []).reduce(
        (total, prodotto) => total + Number(prodotto.prezzoIndicativo || 0) * Number(prodotto.quantita || 0),
        0
      );
      const shippingCost = Number(existingOffer?.costoSpedizione || 0);
      const existingSubtotal = Number(existingOffer?.prezzoSubtotale || existingOffer?.prezzoTotale || 0) - shippingCost;
      const orderDiscountAmount =
        existingOffer && baseTotal > 0
          ? Math.max(0, Number((baseTotal - Math.max(0, existingSubtotal)).toFixed(2)))
          : 0;
      const orderDiscountPercent =
        existingOffer && baseTotal > 0
          ? Math.max(0, Math.min(100, Math.round((orderDiscountAmount / baseTotal) * 100)))
          : 0;

      this.setState({
        richiesta,
        productDiscounts: discounts,
        orderDiscountPercent: orderDiscountPercent ? String(orderDiscountPercent) : "",
        orderDiscountAmount: orderDiscountAmount ? String(orderDiscountAmount) : "",
        shippingCost: shippingCost ? String(shippingCost) : "",
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        isLoading: false,
        error: "Impossibile caricare il dettaglio della richiesta.",
      });
    }
  };

  onProductDiscountPercentChange = (productKey, value, baseLine) => {
    const percentValue = Math.max(0, Math.min(100, Number(value || 0)));
    const amountValue = Number(((baseLine * percentValue) / 100).toFixed(2));
    this.setState((prevState) => ({
      productDiscounts: {
        ...prevState.productDiscounts,
        [productKey]: {
          percent: value,
          amount: String(amountValue),
        },
      },
      submitError: "",
      submitSuccess: "",
    }));
  };

  onProductDiscountAmountChange = (productKey, value, baseLine) => {
    const amountValue = Math.max(0, Math.min(baseLine, Number(value || 0)));
    const percentValue = baseLine > 0 ? Math.round((amountValue / baseLine) * 100) : 0;
    this.setState((prevState) => ({
      productDiscounts: {
        ...prevState.productDiscounts,
        [productKey]: {
          percent: String(percentValue),
          amount: value,
        },
      },
      submitError: "",
      submitSuccess: "",
    }));
  };

  onOrderDiscountPercentChange = (event, baseTotal) => {
    const percentValue = Math.max(0, Math.min(100, Number(event.target.value || 0)));
    const amountValue = Number(((baseTotal * percentValue) / 100).toFixed(2));
    this.setState({
      orderDiscountPercent: event.target.value,
      orderDiscountAmount: String(amountValue),
      submitError: "",
      submitSuccess: "",
    });
  };

  onOrderDiscountAmountChange = (event, baseTotal) => {
    const amountValue = Math.max(0, Math.min(baseTotal, Number(event.target.value || 0)));
    const percentValue = baseTotal > 0 ? Math.round((amountValue / baseTotal) * 100) : 0;
    this.setState({
      orderDiscountAmount: event.target.value,
      orderDiscountPercent: String(percentValue),
      submitError: "",
      submitSuccess: "",
    });
  };

  onShippingCostChange = (event) => {
    this.setState({ shippingCost: event.target.value, submitError: "", submitSuccess: "" });
  };

  getOfferPreview = () => {
    const richiesta = this.state.richiesta;
    if (!richiesta) {
      return { rows: [], total: 0, baseTotal: 0 };
    }

      const rows = (richiesta.prodotti || []).map((prodotto) => {
        const key = prodotto.id || prodotto.nome;
        const baseUnitPrice = Number(prodotto.prezzoIndicativo || 0);
        const quantity = Number(prodotto.quantita || 0);
        const lineBase = baseUnitPrice * quantity;
        const currentDiscount = this.state.productDiscounts[key] || {};
        const lineDiscountPercent = Math.max(0, Math.min(100, Number(currentDiscount.percent || 0)));
        const lineDiscountAmount = Math.max(
          0,
          Math.min(lineBase, Number(currentDiscount.amount || ((lineBase * lineDiscountPercent) / 100)))
        );
        const discountedLine = Math.max(0, lineBase - lineDiscountAmount);

        return {
          descrizione: prodotto.nome,
          quantita: quantity,
          prezzoBase: Number(lineBase.toFixed(2)),
          prezzo: Number(discountedLine.toFixed(2)),
          scontoPercentuale: lineDiscountPercent,
          scontoValore: Number(lineDiscountAmount.toFixed(2)),
        };
      });

    const baseTotal = Number(rows.reduce((total, row) => total + row.prezzoBase, 0).toFixed(2));
    const subtotal = Number(rows.reduce((total, row) => total + row.prezzo, 0).toFixed(2));
    const lineDiscountTotal = Number(rows.reduce((total, row) => total + Number(row.scontoValore || 0), 0).toFixed(2));
    const orderDiscountAmount = Math.max(0, Math.min(subtotal, Number(this.state.orderDiscountAmount || 0)));
    const orderDiscountPercent = subtotal > 0 ? Math.round((orderDiscountAmount / subtotal) * 100) : 0;
    const shippingCost = Math.max(0, Number(this.state.shippingCost || 0));
    const totalBeforeShipping = Math.max(0, subtotal - orderDiscountAmount);
    const total = totalBeforeShipping + shippingCost;

    return {
      rows,
      baseTotal,
      subtotal,
      lineDiscountTotal,
      orderDiscountAmount: Number(orderDiscountAmount.toFixed(2)),
      orderDiscountPercent,
      totalDiscountAmount: Number((lineDiscountTotal + orderDiscountAmount).toFixed(2)),
      shippingCost: Number(shippingCost.toFixed(2)),
      totalBeforeShipping: Number(totalBeforeShipping.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  };

  onSubmitOffer = async () => {
    const { richiesta } = this.state;
    if (!richiesta || isRichiestaExpired(richiesta)) {
      return;
    }

    const seller = this.getSellerIdentity();
    const preview = this.getOfferPreview();
    const existingOffers = richiesta.offerte || [];
    const currentOfferIndex = existingOffers.findIndex((item) => item.sellerId === seller.id);
    const offer = {
      id: currentOfferIndex >= 0 ? existingOffers[currentOfferIndex].id : `off_${Date.now()}`,
      sellerId: seller.id,
      sellerName: seller.name,
      prezzoTotale: preview.total,
      prezzoSubtotale: preview.totalBeforeShipping,
      distanzaKm: 0,
      tempoStimatoMin: 0,
      costoSpedizione: preview.shippingCost,
      scontoOrdinePercentuale: preview.orderDiscountPercent,
      scontoOrdineValore: preview.orderDiscountAmount,
      stato: "INVIATA",
      letta: false,
      createdAt: currentOfferIndex >= 0 ? existingOffers[currentOfferIndex].createdAt : new Date().toISOString(),
      voci: preview.rows.map((row) => ({
        descrizione: row.descrizione,
        prezzo: row.prezzo,
        prezzoBase: row.prezzoBase,
        quantita: row.quantita,
        scontoPercentuale: row.scontoPercentuale,
        scontoValore: row.scontoValore,
      })),
    };

    const isEditingExistingOffer = currentOfferIndex >= 0;

    const offerte =
      currentOfferIndex >= 0
        ? existingOffers.map((item, index) => (index === currentOfferIndex ? offer : item))
        : [...existingOffers, offer];

    const sellers = (richiesta.sellers || []).map((item) =>
      item.sellerUserId === seller.id
        ? {
            ...item,
            status: "OFFERTA_INVIATA",
            viewedAt: item.viewedAt || new Date().toISOString(),
          }
        : item
    );

    this.setState({ isSubmitting: true, submitError: "", submitSuccess: "", takeChargeError: "", takeChargeSuccess: "" });

    try {
      const updated = await RichiesteService.update(richiesta._id, {
        ...richiesta,
        offerte,
        sellers,
        unreadOffersCount: offerte.length,
      });

      this.setState({
        richiesta: updated,
        isSubmitting: false,
        submitSuccess: isEditingExistingOffer ? "Offerta aggiornata correttamente." : "Offerta inviata correttamente al cliente.",
      });
    } catch (error) {
      this.setState({
        isSubmitting: false,
        submitError: "Invio offerta non riuscito. Riprova tra poco.",
      });
    }
  };

  onTakeInCharge = async () => {
    const { richiesta } = this.state;
    if (!richiesta || isRichiestaExpired(richiesta)) {
      return;
    }

    const timestamp = new Date().toISOString();
    this.setState({ isTakingCharge: true, takeChargeError: "", takeChargeSuccess: "", submitError: "", submitSuccess: "" });

    try {
      const updated = await RichiesteService.update(richiesta._id, {
        ...richiesta,
        stato: "IN_LAVORAZIONE",
        tracking: [
          ...(Array.isArray(richiesta.tracking) ? richiesta.tracking : []),
          {
            stato: "IN_LAVORAZIONE",
            timestamp,
            note: "Ordine preso in carico dal venditore dopo conferma pagamento.",
          },
        ],
      });

      this.setState({
        richiesta: updated,
        isTakingCharge: false,
        takeChargeSuccess: "Ordine preso in carico correttamente. Ora puoi procedere con l'evasione.",
      });
    } catch (error) {
      this.setState({
        isTakingCharge: false,
        takeChargeError: "Non siamo riusciti a prendere in carico l'ordine. Riprova tra poco.",
      });
    }
  };

  render() {
    const {
      richiesta,
      isLoading,
      error,
      isSubmitting,
      isTakingCharge,
      submitError,
      submitSuccess,
      takeChargeError,
      takeChargeSuccess,
    } = this.state;
    const preview = this.getOfferPreview();
    const paymentState = richiesta?.pagamento?.stato || "NON_AVVIATO";
    const acceptedOfferId = richiesta?.offertaAccettataId || null;
    const seller = this.getSellerIdentity();
    const sellerOffer = (richiesta?.offerte || []).find((item) => item.sellerId === seller.id) || null;
    const sellerOfferId = sellerOffer?.id || seller.id;
    const isSellerAccepted = Boolean(acceptedOfferId) && [sellerOfferId, seller.id].includes(acceptedOfferId);
    const isPaid = paymentState === "PAGATO";
    const isPaymentPending = paymentState === "IN_ATTESA";
    const isAwaitingPaymentSelection = richiesta?.stato === "IN_ATTESA_PAGAMENTO" && !isPaymentPending && !isPaid;
    const isInProgress = richiesta?.stato === "IN_LAVORAZIONE";
    const isExpired = isRichiestaExpired(richiesta);
    const expiresAt = getRichiestaExpiresAt(richiesta);
    const customer = richiesta?.customerSnapshot || {};
    const lockOfferEditing = isExpired || Boolean(acceptedOfferId) || isPaymentPending || isPaid || isInProgress;
    const canTakeInCharge = !isExpired && isSellerAccepted && isPaid && !isInProgress;
    const isAssignedToAnotherSeller = Boolean(acceptedOfferId) && !isSellerAccepted;
    const requestTitle = getRequestDisplayTitle(richiesta);
    const hasSellerOffer = Boolean(sellerOffer);
    const offerFeedbackPanel = getSellerOfferFeedbackPanel({
      isAssignedToAnotherSeller,
      lockOfferEditing,
      isPaid,
      isInProgress,
    });
    const orderStatePanel = getSellerOrderStatePanel({
      isAssignedToAnotherSeller,
      isInProgress,
      isPaid,
      isPaymentPending,
      isSellerAccepted,
    });
    if (!isAssignedToAnotherSeller && lockOfferEditing && !isPaid && !isInProgress) {
      offerFeedbackPanel.title = "Offerta selezionata";
      offerFeedbackPanel.description =
        "Il cliente ha selezionato la tua offerta, ma l'ordine non è ancora confermato: siamo in attesa del pagamento.";
    }
    if (isAssignedToAnotherSeller && isAwaitingPaymentSelection) {
      offerFeedbackPanel.title = "Offerta non selezionata";
      offerFeedbackPanel.description =
        "Il cliente ha selezionato un'altra proposta. L'ordine non è ancora confermato finche non viene completato il pagamento.";
    }
    if (!isAssignedToAnotherSeller && isSellerAccepted && !isPaymentPending && !isPaid && !isInProgress) {
      orderStatePanel.title = "In attesa di pagamento";
      orderStatePanel.description =
        "L'offerta è stata selezionata dal cliente. L'ordine verrà confermato solo dopo il pagamento.";
    }
    const sellerStatusPanel = isSellerAccepted ? orderStatePanel : offerFeedbackPanel;
    const submitOfferButtonState = getSellerSubmitOfferButtonState({
      isSubmitting,
      lockOfferEditing,
      isInProgress,
      isPaid,
      isPaymentPending,
    });
    return (
      <div className="seller-shell">
        <main className="seller-main">
          <PageHero
            className="seller-hero seller-hero--detail"
            eyebrow="Dettaglio richiesta"
            title={requestTitle}
            description="Consulta i prodotti richiesti, definisci la tua proposta economica e monitora lo stato operativo della richiesta."
            stacked={true}
            linkBack={`${process.env.PUBLIC_URL}/seller-richieste`}
            backLabel="Torna alle richieste"
          />

          {error ? <div className="alert alert-danger">{error}</div> : null}
          {!isLoading && richiesta && isExpired ? (
            <div className="alert alert-warning">
              Richiesta scaduta: la finestra di 24 ore e terminata
              {expiresAt ? ` (${formatDateTime(expiresAt.toISOString())}).` : "."}
            </div>
          ) : null}

          {isLoading ? (
            <EmptyStateCard
              className="seller-empty-state"
              icon="fa-circle-o-notch"
              spin={true}
              title="Caricamento dettaglio"
              description="Stiamo recuperando i prodotti e lo stato della richiesta."
            />
          ) : null}

          {!isLoading && richiesta ? (
            <>
              {hasSellerOffer ? (
                <div className="seller-status-panels">
                  <StatusPanel
                    className={sellerStatusPanel.className}
                    tone={sellerStatusPanel.tone}
                    eyebrow={sellerStatusPanel.eyebrow}
                    title={sellerStatusPanel.title}
                    description={sellerStatusPanel.description}
                    spinning={isPaymentPending}
                  />
                </div>
              ) : null}

              <SurfaceCard
                className="seller-customer-card"
                eyebrow="Dati spedizione"
                title="Contatto cliente"
              >
                <div className="seller-customer-card__grid">
                  <div>
                    <span>Cliente</span>
                    <strong>
                      {customer.fullName ||
                        [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
                        "Cliente marketplace"}
                    </strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <strong>{customer.email || "Non disponibile"}</strong>
                  </div>
                  <div>
                    <span>Indirizzo</span>
                    <strong>{customer.address || "Indirizzo non disponibile"}</strong>
                  </div>
                </div>
              </SurfaceCard>

              <div className="seller-detail-grid">
              <SurfaceCard
                className="seller-detail-card seller-detail-card--products"
                eyebrow="Prodotti richiesti"
                title="Applica gli sconti"
              >
                <div className="seller-offer-list">
                  {(richiesta.prodotti || []).map((prodotto) => {
                    const key = prodotto.id || prodotto.nome;
                    const quantity = Number(prodotto.quantita || 0);
                    const baseUnit = Number(prodotto.prezzoIndicativo || 0);
                    const lineBase = baseUnit * quantity;
                    const discountState = this.state.productDiscounts[key] || {};
                    const lineDiscountPercent = Number(discountState.percent || 0);
                    const lineDiscountAmount = Math.max(0, Math.min(lineBase, Number(discountState.amount || 0)));
                    const lineTotal = Math.max(0, lineBase - lineDiscountAmount);

                    return (
                      <div className="seller-offer-row" key={key}>
                        <div className="seller-offer-row__main">
                          <h4>{prodotto.nome}</h4>
                          <p>
                            {quantity} conf. x {formatPrice(baseUnit)}
                          </p>
                        </div>
                        <div className="seller-offer-row__controls">
                          <label>Sconto %</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="form-control"
                            value={discountState.percent || ""}
                            onChange={(event) => this.onProductDiscountPercentChange(key, event.target.value, lineBase)}
                            disabled={lockOfferEditing}
                          />
                        </div>
                        <div className="seller-offer-row__controls">
                          <label>Sconto EUR</label>
                          <input
                            type="number"
                            min="0"
                            max={lineBase}
                            step="0.01"
                            className="form-control"
                            value={discountState.amount || ""}
                            onChange={(event) => this.onProductDiscountAmountChange(key, event.target.value, lineBase)}
                            disabled={lockOfferEditing}
                          />
                        </div>
                        <div className="seller-offer-row__summary">
                          <span>Base {formatPrice(lineBase)}</span>
                          <strong>{formatPrice(lineTotal)}</strong>
                          <small>
                            Sconto {lineDiscountPercent || 0}% - {formatPrice(lineDiscountAmount)}
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SurfaceCard>

              <SurfaceCard
                as="aside"
                className="seller-detail-card seller-detail-card--summary"
                eyebrow="Riepilogo offerta"
                title="Prezzo finale"
              >

                <DetailMetricCard className="seller-offer-summary-metric" label="Totale base" value={formatPrice(preview.baseTotal)} />

                <DetailMetricCard
                  className="seller-offer-summary-metric"
                  label="Subtotale dopo sconti prodotto"
                  value={formatPrice(preview.subtotal)}
                />

                <div className="form-group seller-summary-input-card">
                  <label>Sconto finale ordine %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="form-control"
                    value={this.state.orderDiscountPercent}
                    onChange={(event) => this.onOrderDiscountPercentChange(event, preview.subtotal)}
                    disabled={lockOfferEditing}
                  />
                </div>

                <div className="form-group seller-summary-input-card">
                  <label>Sconto finale ordine EUR</label>
                  <input
                    type="number"
                    min="0"
                    max={preview.subtotal}
                    step="0.01"
                    className="form-control"
                    value={this.state.orderDiscountAmount}
                    onChange={(event) => this.onOrderDiscountAmountChange(event, preview.subtotal)}
                    disabled={lockOfferEditing}
                  />
                </div>

                <div className="form-group seller-summary-input-card">
                  <label>Costo spedizione</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={this.state.shippingCost}
                    onChange={this.onShippingCostChange}
                    disabled={lockOfferEditing}
                  />
                </div>

                <DetailMetricCard
                  className="seller-offer-summary-metric"
                  label="Sconto totale"
                  value={formatPrice(preview.totalDiscountAmount)}
                  helper={`${formatPrice(preview.lineDiscountTotal)} prodotti + ${formatPrice(preview.orderDiscountAmount)} ordine`}
                />
                <DetailMetricCard className="seller-offer-summary-metric" label="Costo spedizione" value={formatPrice(preview.shippingCost)} />
                <div className="seller-offer-total-box">
                  <span>Totale offerta</span>
                  <strong>{formatPrice(preview.total)}</strong>
                </div>

                {submitError ? <div className="alert alert-danger m-t-15">{submitError}</div> : null}
                {submitSuccess ? <div className="alert alert-success m-t-15">{submitSuccess}</div> : null}
                {takeChargeError ? <div className="alert alert-danger m-t-15">{takeChargeError}</div> : null}
                {takeChargeSuccess ? <div className="alert alert-success m-t-15">{takeChargeSuccess}</div> : null}

                {canTakeInCharge ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-block seller-offer-submit seller-offer-submit--take-charge"
                    onClick={this.onTakeInCharge}
                    disabled={isTakingCharge}
                  >
                    <i className={`fa ${isTakingCharge ? "fa-circle-o-notch fa-spin" : "fa-briefcase"} m-r-10`}></i>
                    
                    {isTakingCharge ? "Presa in carico in corso..." : "Prendi in carico ordine"}
                  </button>
                ) : (
                    <button
                      type="button"
                      className="btn btn-primary btn-block seller-offer-submit"
                      onClick={this.onSubmitOffer}
                      disabled={isSubmitting || lockOfferEditing}
                    >
                      <i className={`fa ${submitOfferButtonState.icon} m-r-10`}></i>
                      {submitOfferButtonState.label}
                    </button>
                  )}
              </SurfaceCard>
              </div>
            </>
          ) : null}
        </main>
      </div>
    );
  }
}

export default RichiestaDettaglioVenditore;


















